import type {ReactRef} from 'src/types/react';

import * as React from 'react';

import makeClassNameComponent, {
  type ClassNameComponent,
} from 'src/utils/makeClassNameComponent';
import {
  useContextMenu,
  type ContextMenuConfig,
  type ContextMenuTuple,
} from './context-menu';
import HoverIconButton from 'src/components/lib/hover-icon-button';
import {
  HoverButton,
  type HoverButtonProps,
} from 'src/components/lib/hover-button/hover-button';
import MenuIcon from 'src/images/icons/options-icon.svg';
import ArrowRight from 'src/images/arrow-right.svg';

import css from './context-menu-2.css';


export function ContextMenu2({
  className,
  children,
  anchor,
  ...config
}: ContextMenuConfig & {
  children?: React.ReactNode;
}) {
  const [componentRef, handleOpen, handleClose, isOpen] =
    useContextMenu2(config);

  return (
    <HoverIconButton
      className={className}
      ref={componentRef}
      onClick={isOpen() ? handleClose : handleOpen}
    >
      {children || anchor || <MenuIcon />}
    </HoverIconButton>
  );
}

export function useContextMenu2(config: ContextMenuConfig): ContextMenuTuple {
  return useContextMenu({
    noArrow: true,
    noStyle: true,
    contextMenuClassName: css.menu,
    ...config,
  });
}

export function useContextMenu3(config: ContextMenuConfig): {
  ref: ReactRef<HTMLElement>;
  onClick: () => void;
} {
  const [ref, handleOpen, handleClose, isOpen] = useContextMenu2(config);
  return {
    ref,
    onClick: isOpen() ? handleClose : handleOpen,
  };
}

export function MenuOption({ElementType = 'div', ...props}: HoverButtonProps) {
  return (
    <HoverButton
      {...props}
      className={css.menuOption}
      justify="start"
      ElementType={ElementType}
    />
  );
}

// @ts-expect-error - TS2322 - Type 'AbstractComponent<ComponentProps<C>, unknown>' is not assignable to type 'ClassNameComponent'.
export const Menu: ClassNameComponent = makeClassNameComponent(css.menu);

export function SubMenuOption({
  children,
  ...props
}: {
  children: string;
  onMouseEnter?:
    | ((arg1: React.MouseEvent<HTMLElement>) => unknown)
    | null
    | undefined;
}) {
  return (
    <MenuOption {...props} isClickable={false}>
      <span>{children}</span> <ArrowRight />
    </MenuOption>
  );
}

export function SuperMenu({
  options,
}: {
  options: Array<
    | {
        label: string;
        action: () => unknown;
      }
    | {
        label: string;
        submenu: React.ReactNode;
      }
  >;
}) {
  const [subMenu, setSubMenu] = React.useState<
    | {
        content: React.ReactNode;
        left: number;
        top: number;
      }
    | null
    | undefined
  >();

  return (
    <>
      {options.map((option) =>
        // @ts-expect-error - TS2339 - Property 'submenu' does not exist on type '{ label: string; action: () => unknown; } | { label: string; submenu: ReactNode; }'.
        option.submenu ? (
          <SubMenuOption
            key={option.label}
            onMouseEnter={(event) => {
              const {
                currentTarget: {offsetLeft, offsetTop, offsetWidth},
              } = event;

              setSubMenu({
                left: offsetLeft + offsetWidth,
                top: offsetTop,
                // @ts-expect-error - TS2339 - Property 'submenu' does not exist on type '{ label: string; action: () => unknown; } | { label: string; submenu: ReactNode; }'.
                content: option.submenu,
              });
            }}
          >
            {option.label}
          </SubMenuOption>
        ) : // @ts-expect-error - TS2339 - Property 'action' does not exist on type '{ label: string; action: () => unknown; } | { label: string; submenu: ReactNode; }'.
        option.action ? (
          <MenuOption
            key={option.label}
            // @ts-expect-error - TS2339 - Property 'action' does not exist on type '{ label: string; action: () => unknown; } | { label: string; submenu: ReactNode; }'.
            onClick={option.action}
            onMouseEnter={() => {
              setSubMenu(null);
            }}
          >
            {option.label}
          </MenuOption>
        ) : null,
      )}
      {subMenu && (
        <Menu
          style={{
            position: 'absolute',
            left: subMenu.left,
            top: subMenu.top,
          }}
        >
          {subMenu.content}
        </Menu>
      )}
    </>
  );
}
